import { TemplatePrompts } from '@klab-berlin/api-sdk/lib/types/common/Conversation';
import i18next from 'i18next';
import { IconName } from '../../../utils/getMuiIcon';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import { INITIAL_POSITION, ActiveView } from './constants';

interface CalculateDraggableBoundariesParams {
  windowWidth: number;
  windowHeight: number;
  elementWidth: number;
  elementHeight: number;
}

export const calculateDraggableBoundaries =
  ({ windowWidth, windowHeight, elementWidth, elementHeight }: CalculateDraggableBoundariesParams) => ({
    left: -(windowWidth - elementWidth - INITIAL_POSITION.right),
    right: INITIAL_POSITION.right,
    top: -INITIAL_POSITION.top,
    bottom: (windowHeight - elementHeight - INITIAL_POSITION.top),
  });

export const assistantTemplatePrompts: Partial<Record<ActiveView, Array<{
  id: TemplatePrompts,
  title: string,
  subheader: string,
  icon: IconName,
  chip?: 'new' | 'beta',
}>>> = {
  [ActiveView.UNIT_ENTRY]: [
    {
      id: TemplatePrompts.CREATE_LEARNING_POSTER,
      title: i18next.t('Create learning poster'),
      subheader: i18next.t('Summarize core content visually'),
      icon: 'boldGallery',
      chip: 'new',
    },
    {
      id: TemplatePrompts.CREATE_SUMMARY,
      title: i18next.t('Write summary'),
      subheader: i18next.t('Summarise content briefly and clearly'),
      icon: 'textDocumentBold',
    },
    {
      id: TemplatePrompts.CREATE_DIFFERENTIATED_TASKS,
      title: i18next.t('Differentiate tasks'),
      subheader: i18next.t('Create three levels'),
      icon: 'shapesBold',
    },
    {
      id: TemplatePrompts.CREATE_QUIZ,
      title: i18next.t('Create quiz'),
      subheader: i18next.t('Check understanding with multiple-choice questions'),
      icon: 'bulbBold',
    },
    {
      id: TemplatePrompts.CREATE_LESSON_PLAN,
      title: i18next.t('Create a lesson plan'),
      subheader: i18next.t('Planning for a 45-minute lesson'),
      icon: 'addDocumentBold',
    },
    {
      id: TemplatePrompts.CREATE_INTRODUCTORY_LESSON,
      title: i18next.t('Formulate initial impulse'),
      subheader: i18next.t('Create an introduction for a lesson'),
      icon: 'slidesBold',
    },
    {
      id: TemplatePrompts.CREATE_ADDITIONAL_TASKS,
      title: i18next.t('Insert extension tasks'),
      subheader: i18next.t('Provide in-depth questions or additional tasks'),
      icon: 'checklistBold',
    },
    {
      id: TemplatePrompts.CREATE_SUPPLEMENTAL_AIDS,
      title: i18next.t('Supplement aids'),
      subheader: i18next.t('Add explanations and tips'),
      icon: 'starWandBold',
    }],
  [ActiveView.FREE_DRAFT_ENTRY]: [
    {
      id: TemplatePrompts.CREATE_WORKSHEET_FREE_DRAFT,
      title: i18next.t('Create worksheet'),
      subheader: i18next.t('Differenzierte Aufgaben'),
      icon: 'createWorksheet',
    },
    {
      id: TemplatePrompts.CREATE_LESSON_PLAN_FREE_DRAFT,
      title: i18next.t('Lesson planning'),
      subheader: i18next.t('Lesson planning for 45 or 90 minutes'),
      icon: 'aiLessonPlanning',
    },
    {
      id: TemplatePrompts.CREATE_GROUP_WORK_FREE_DRAFT,
      title: i18next.t('Group work'),
      subheader: i18next.t('For effective team learning'),
      icon: 'aiGroupTasks',
    },
    {
      id: TemplatePrompts.CREATE_ESSAY_FREE_DRAFT,
      title: i18next.t('Essay'),
      subheader: i18next.t('Example essay for analysis'),
      icon: 'essayTemplate',
    },
    {
      id: TemplatePrompts.CREATE_TOPIC_EXPLANATION_FREE_DRAFT,
      title: i18next.t('Topic explanation'),
      subheader: i18next.t('Simple explanations'),
      icon: 'explainTopic',
    },
    {
      id: TemplatePrompts.CREATE_LEARNING_OBJECTIVE_ASSERTION_FREE_DRAFT,
      title: i18next.t('Learning objective assertion'),
      subheader: i18next.t('Query for knowledge verification'),
      icon: 'aiLearningObjectiveGeneration',
    },
    {
      id: TemplatePrompts.CREATE_LESSON_SERIES_FREE_DRAFT,
      title: i18next.t('Lesson series planning'),
      subheader: i18next.t('Planning multiple hours'),
      icon: 'aiLessonSeriesPlanning',
    },
  ],
};

export interface MediaCreationTool {
  id: TemplatePrompts;
  title: string;
  subheader: string;
  icon: IconName;
  chip?: 'new' | 'beta';
}

export const mediaCreationTools: MediaCreationTool[] = [
  {
    id: TemplatePrompts.CREATE_IMAGE,
    title: i18next.t('Generate image'),
    subheader: i18next.t('Quickly create suitable classroom images'),
    icon: 'boldGallery',
  },
];

export const podcastTools: MediaCreationTool[] = [
  {
    id: TemplatePrompts.CREATE_PODCAST,
    title: i18next.t('Generate podcast'),
    subheader: i18next.t('Generate audio teaching materials'),
    icon: 'microphone',
    chip: 'beta',
  },
];

/**
 * Formats an array of numbers into a string representation of ranges
 * Example: [1,2,3,5,6,7,8,9,10] => "1-3 • 5-10"
 * @param numbers - Array of numbers to format
 * @returns Formatted string of ranges
 */
export const formatNumberRanges = (numbers: number[]): string => {
  if (!numbers.length) return '';

  // Sort numbers to ensure they're in order
  const sortedNumbers = [...numbers].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sortedNumbers[0];
  let prev = start;

  for (let i = 1; i <= sortedNumbers.length; i++) {
    const current = sortedNumbers[i];

    // If we're at the end or there's a gap
    if (i === sortedNumbers.length || current !== prev + 1) {
      if (start === prev) {
        ranges.push(start.toString());
      } else {
        ranges.push(`${start}-${prev}`);
      }
      start = current;
    }
    prev = current;
  }

  return ranges.join(', ');
};

export const IMAGE_CREATION_TEMPLATE_PROMPTS: TemplatePrompts[] = [
  TemplatePrompts.CREATE_LEARNING_POSTER,
  TemplatePrompts.CREATE_IMAGE,
];

export const isImageGenerationTemplate = (id: TemplatePrompts): boolean =>
  IMAGE_CREATION_TEMPLATE_PROMPTS.includes(id);

export const getTemplatePromptsByView = (view: ActiveView) => {
  return assistantTemplatePrompts[view] || [];
};

// illegal characters break the downloaded file name in Safari
export const sanitizeFilename = (name: string) => {
  return name
    .replace(/\s+/g, ' ')                       // collapse whitespace/newlines
    // eslint-disable-next-line no-control-regex
    .replace(/[/\\:*?"<>|\x00-\x1f]/g, '')      // remove filesystem-illegal chars
    .trim()
    .replace(/\s/g, '_');                       // spaces → underscores
};

// Fetches a remote file and saves it via a temporary anchor so we control the
// filename. Needed because a cross-origin, pre-signed URL ignores the anchor's
// `download` attribute — only a same-origin blob URL honours it.
const downloadFromUrl = async (src: string, filename: string): Promise<void> => {
  try {
    const res = await fetch(src);
    if (!res.ok) {
      throw new Error(`download fetch failed: ${res.status} ${res.statusText}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    notifyBugsnagHandledError(error as Error);
    // Cross-origin without CORS — let the user save it manually from a new tab.
    const popup = window.open(src, '_blank', 'noopener,noreferrer');
    if (!popup) {
      notifyBugsnagHandledError(
        new Error('download fallback failed: window.open returned null (popup blocked)'),
      );
    }
  }
};

export const downloadImage = async (src: string, alt?: string): Promise<void> => {
  const sanitizedName = sanitizeFilename(alt || 'image');
  await downloadFromUrl(src, sanitizedName || 'image');
};

export const downloadAudio = async (src: string, title?: string): Promise<void> => {
  const sanitizedName = sanitizeFilename(title || 'podcast');
  await downloadFromUrl(src, `${sanitizedName || 'podcast'}.wav`);
};
